import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Loader2, MessageSquareWarning } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { getErrorMessage } from '@/lib/api'
import { formatDate } from '@/lib/utils'
import { getTicketNumber } from '@/features/complaints/conversation-meta'
import { ComplaintConversation, STATUS_LABELS, statusBadgeVariant } from '@/features/complaints/complaint-conversation'
import {
  complaintsApi,
  type ComplaintSubjectKind,
  type ComplaintCategory,
} from '@/services/complaints'
import type { Job } from '@/types'

const CATEGORY_LABELS: Record<ComplaintCategory, string> = {
  service_quality: 'Service Quality',
  worker_conduct: 'Worker Conduct',
  billing: 'Billing',
  scheduling: 'Scheduling',
  damage: 'Damage',
  other: 'Other',
}

interface ComplaintsPanelProps {
  job?: Job
  bookingId?: string | number
  enquiryId?: string | number
  quotationId?: string | number
  invoiceId?: string | number
  /** Tenant IANA timezone for timestamps; omitted keeps the existing browser-timezone output. */
  timeZone?: string | null
}

type SubjectContext = {
  kind: ComplaintSubjectKind
  id: string | number
  listParams: {
    job_id?: string | number
    booking_id?: string | number
    enquiry_id?: string | number
    quotation_id?: string | number
    invoice_id?: string | number
    page_size: number
  }
  createParams: {
    job_id?: string | number
    booking_id?: string | number
    enquiry_id?: string | number
    quotation_id?: string | number
    invoice_id?: string | number
  }
}

const resolveSubjectContext = ({
  job,
  bookingId,
  enquiryId,
  quotationId,
  invoiceId,
}: ComplaintsPanelProps): SubjectContext | null => {
  if (job?.id != null) {
    return {
      kind: 'job',
      id: job.id,
      listParams: { job_id: job.id, page_size: 50 },
      createParams: { job_id: job.id },
    }
  }

  if (bookingId != null) {
    return {
      kind: 'booking',
      id: bookingId,
      listParams: { booking_id: bookingId, page_size: 50 },
      createParams: { booking_id: bookingId },
    }
  }

  if (enquiryId != null) {
    return {
      kind: 'enquiry',
      id: enquiryId,
      listParams: { enquiry_id: enquiryId, page_size: 50 },
      createParams: { enquiry_id: enquiryId },
    }
  }

  if (quotationId != null) {
    return {
      kind: 'quotation',
      id: quotationId,
      listParams: { quotation_id: quotationId, page_size: 50 },
      createParams: { quotation_id: quotationId },
    }
  }

  if (invoiceId != null) {
    return {
      kind: 'invoice',
      id: invoiceId,
      listParams: { invoice_id: invoiceId, page_size: 50 },
      createParams: { invoice_id: invoiceId },
    }
  }

  return null
}

export function ComplaintsPanel({ job, bookingId, enquiryId, quotationId, invoiceId, timeZone }: ComplaintsPanelProps) {
  const formatTimestamp = (value: string) => formatDate(value, timeZone ? { timeZone } : undefined)
  const subjectContext = resolveSubjectContext({ job, bookingId, enquiryId, quotationId, invoiceId })
  const contextKey = subjectContext?.id ?? null
  const queryClient = useQueryClient()
  const [selectedComplaintId, setSelectedComplaintId] = useState<string | number | null>(null)
  const [selectionKey, setSelectionKey] = useState(0)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newSubject, setNewSubject] = useState('')
  const [newCategory, setNewCategory] = useState<ComplaintCategory>('other')
  const [newDescription, setNewDescription] = useState('')

  if (subjectContext == null || contextKey == null) {
    return (
      <Card>
        <CardContent className="py-8 text-sm text-muted-foreground">
          A valid job, booking, enquiry, quotation, or invoice is required to manage complaints.
        </CardContent>
      </Card>
    )
  }

  const complaintsQuery = useQuery({
    queryKey: ['complaints', contextKey, subjectContext.kind],
    queryFn: () => complaintsApi.list(subjectContext.listParams),
    enabled: contextKey != null,
  })

  const complaints = complaintsQuery.data?.results ?? []
  const selectedComplaint = useMemo(
    () => complaints.find((item) => String(item.id) === String(selectedComplaintId)) ?? null,
    [complaints, selectedComplaintId],
  )

  const invalidateComplaints = () => {
    queryClient.invalidateQueries({ queryKey: ['complaints', contextKey] })
    if (selectedComplaintId != null) {
      queryClient.invalidateQueries({ queryKey: ['complaint-messages', selectedComplaintId] })
      queryClient.invalidateQueries({ queryKey: ['complaint-follow-ups', selectedComplaintId] })
    }
  }

  const createMutation = useMutation({
    mutationFn: complaintsApi.create,
    onSuccess: (created) => {
      toast.success(`Ticket ${getTicketNumber(created)} created`)
      setShowCreateForm(false)
      setNewSubject('')
      setNewDescription('')
      setSelectedComplaintId(created.id)
      invalidateComplaints()
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const handleCreate = () => {
    const subject = newSubject.trim()
    if (!subject) {
      toast.error('Subject is required')
      return
    }
    createMutation.mutate({
      ...subjectContext.createParams,
      subject,
      category: newCategory,
      description: newDescription.trim(),
      body: newDescription.trim() || subject,
    })
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-base">Support tickets</CardTitle>
              <CardDescription>Each complaint is tracked as a numbered ticket with status and follow-ups</CardDescription>
            </div>
            <Button size="sm" onClick={() => setShowCreateForm((value) => !value)}>
              New
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {showCreateForm ? (
            <div className="space-y-3 rounded-xl border border-border/70 p-3">
              <div className="space-y-2">
                <Label htmlFor="complaint-subject">Subject</Label>
                <Input
                  id="complaint-subject"
                  value={newSubject}
                  onChange={(event) => setNewSubject(event.target.value)}
                  placeholder="Brief summary"
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={newCategory} onValueChange={(value) => setNewCategory(value as ComplaintCategory)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="complaint-description">Initial message</Label>
                <Textarea
                  id="complaint-description"
                  rows={4}
                  value={newDescription}
                  onChange={(event) => setNewDescription(event.target.value)}
                  placeholder="Describe the issue in detail..."
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowCreateForm(false)}>Cancel</Button>
                <Button size="sm" disabled={createMutation.isPending} onClick={handleCreate}>
                  {createMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Create
                </Button>
              </div>
            </div>
          ) : null}

          {complaintsQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : complaints.length === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon"><MessageSquareWarning /></EmptyMedia>
                <EmptyTitle>No complaints</EmptyTitle>
                <EmptyDescription>
                  Complaints raised from the customer app, worker app, or portal will appear here.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <div className="space-y-2">
              {complaints.map((complaint) => (
                <button
                  key={complaint.id}
                  type="button"
                  onClick={() => {
                    setSelectedComplaintId(complaint.id)
                    setSelectionKey((key) => key + 1)
                  }}
                  className={`w-full rounded-xl border px-3 py-3 text-left transition-colors ${
                    String(selectedComplaintId) === String(complaint.id)
                      ? 'border-primary bg-primary/5'
                      : 'border-border/70 hover:bg-muted/40'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium text-foreground">{complaint.subject}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {getTicketNumber(complaint)}
                        {' · '}
                        {CATEGORY_LABELS[complaint.category] ?? complaint.category}
                        {complaint.assigned_to_name ? ` · ${complaint.assigned_to_name}` : ''}
                      </p>
                    </div>
                    <Badge variant={statusBadgeVariant(complaint.status)}>
                      {complaint.status_label || STATUS_LABELS[complaint.status]}
                    </Badge>
                  </div>
                  <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{complaint.description}</p>
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    {complaint.message_count ?? 0} messages
                    {complaint.last_message_at ? ` · ${formatTimestamp(complaint.last_message_at)}` : ''}
                  </p>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {selectedComplaint ? (
        <ComplaintConversation
          complaint={selectedComplaint}
          timeZone={timeZone}
          selectionKey={selectionKey}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ['complaints', contextKey] })}
        />
      ) : (
        <Card className="min-h-[520px]">
          <CardHeader>
            <CardTitle className="text-base">Conversation</CardTitle>
            <CardDescription>Select a complaint to view the thread</CardDescription>
          </CardHeader>
          <CardContent className="flex h-full flex-col">
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon"><MessageSquareWarning /></EmptyMedia>
                <EmptyTitle>Select a complaint</EmptyTitle>
                <EmptyDescription>
                  Choose a complaint from the list to read and reply to the conversation.
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
